import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["id", "name", "errorText", "helperText", "disableErrorContainer", "disabled", "className", "children", "checked", "indeterminate", "setIndeterminate", "onBlur", "onFocus", "onChange", "$css"];
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Div, Span } from '../../../Styled';
import { flex, focusStylesDark, hoverChildMultiple, isKeyboard, TRANSITION_DEFAULT } from '../../../../styling';
import { COLORS, TYPOGRAPHY } from '../../../../themes';
import { InputError as BaseInputError } from '../Error';
import { addInputErrorAriaProps, dotToDash } from '../../../../utils';
import { HelperText } from '../../../text';
import { Check, MinusIndeterminate } from '../../../svg';
var Wrapper = /*#__PURE__*/styled(Div).withConfig({
  displayName: "Checkbox__Wrapper",
  componentId: "sc-1pzil20-0"
})(["position:relative;display:inline-block;"]);
var ContentWrapper = /*#__PURE__*/styled(Div).withConfig({
  displayName: "Checkbox__ContentWrapper",
  componentId: "sc-1pzil20-1"
})(["", ";", " ", ""], flex('flex-start', 'flex-start'), function (_ref) {
  var $checked = _ref.$checked,
      $error = _ref.$error;
  return hoverChildMultiple('> div', [{
    prop: 'background',
    base: $checked ? $error ? COLORS.NEGATIVE_500 : COLORS.PRIMARY_500 : 'none',
    hover: $error ? COLORS.NEGATIVE_400 : $checked ? COLORS.PRIMARY_400 : COLORS.PRIMARY_300,
    active: $checked ? COLORS.PRIMARY_600 : COLORS.PRIMARY_300
  }, {
    prop: 'border-color',
    base: $error ? COLORS.NEGATIVE_500 : COLORS.PRIMARY_500,
    hover: $error ? COLORS.NEGATIVE_400 : $checked ? COLORS.PRIMARY_400 : COLORS.PRIMARY_500,
    active: COLORS.PRIMARY_600
  }]);
}, function (_ref2) {
  var $disabled = _ref2.$disabled;
  return $disabled && css(["pointer-events:none;> div{border-color:", ";background:", ";}"], COLORS.NEUTRAL_300, COLORS.NEUTRAL_300);
});
export var StyledCheckbox = /*#__PURE__*/styled.div.withConfig({
  displayName: "Checkbox__StyledCheckbox",
  componentId: "sc-1pzil20-2"
})(["position:absolute;top:0;left:0;pointer-events:none;border-style:solid;border-width:2px;border-radius:2px;width:2.4rem;height:2.4rem;box-sizing:border-box;", ";", ";svg{transform-origin:50% 50% 0;transition:transform ", ";", "}"], flex(), function (_ref3) {
  var $focus = _ref3.$focus,
      $error = _ref3.$error;
  return $focus && css(["", ";", ";"], focusStylesDark, $error && "box-shadow: 0 0 0 4px ".concat(COLORS.NEGATIVE_700));
}, TRANSITION_DEFAULT, function (_ref4) {
  var $checked = _ref4.$checked;
  return $checked ? css(["transform:none;"]) : css(["transform:scale(0);"]);
});
var NativeInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "Checkbox__NativeInput",
  componentId: "sc-1pzil20-3"
})(["width:2.4rem;height:2.4rem;margin-right:12px;flex:0 0 auto;"]);
var Label = /*#__PURE__*/styled.label.withConfig({
  displayName: "Checkbox__Label",
  componentId: "sc-1pzil20-4"
})(["cursor:pointer;> *{&:first-child{padding-top:0.2rem;}}"]);
var InputError = /*#__PURE__*/styled(BaseInputError).withConfig({
  displayName: "Checkbox__InputError",
  componentId: "sc-1pzil20-5"
})(["padding-left:4rem;"]);

var Checkbox = function Checkbox(_ref5) {
  var id = _ref5.id,
      name = _ref5.name,
      errorText = _ref5.errorText,
      helperText = _ref5.helperText,
      _ref5$disableErrorCon = _ref5.disableErrorContainer,
      disableErrorContainer = _ref5$disableErrorCon === void 0 ? false : _ref5$disableErrorCon,
      disabled = _ref5.disabled,
      className = _ref5.className,
      children = _ref5.children,
      _ref5$checked = _ref5.checked,
      checked = _ref5$checked === void 0 ? false : _ref5$checked,
      _ref5$indeterminate = _ref5.indeterminate,
      indeterminate = _ref5$indeterminate === void 0 ? false : _ref5$indeterminate,
      setIndeterminate = _ref5.setIndeterminate,
      _onBlur = _ref5.onBlur,
      _onFocus = _ref5.onFocus,
      _onChange = _ref5.onChange,
      $css = _ref5.$css,
      rest = _objectWithoutProperties(_ref5, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focus = _useState2[0],
      setFocus = _useState2[1];

  var _useState3 = useState(checked),
      _useState4 = _slicedToArray(_useState3, 2),
      localChecked = _useState4[0],
      setLocalChecked = _useState4[1];

  var isString = typeof children === 'string';
  var idRef = useRef(dotToDash(id || name)).current; // When checkbox is updated outside of user clicking update localChecked so that UI changes

  useEffect(function () {
    if (localChecked !== checked) setLocalChecked(checked);
  }, [localChecked, checked]);
  useEffect(function () {
    if ((localChecked || checked) && setIndeterminate) {
      setIndeterminate(false);
    }
  }, [checked, localChecked, setIndeterminate]);
  return /*#__PURE__*/React.createElement(Wrapper, {
    className: className,
    $css: $css
  }, /*#__PURE__*/React.createElement(ContentWrapper, {
    $error: !!errorText,
    $checked: localChecked,
    $disabled: disabled
  }, /*#__PURE__*/React.createElement(NativeInput, _extends({
    name: name,
    onChange: function onChange(e) {
      var _e$target;

      if (_onChange) {
        _onChange(e);
      }

      setLocalChecked((_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.checked);
      if (setIndeterminate) setIndeterminate(false);
    },
    onFocus: function onFocus(e) {
      if (_onFocus) {
        _onFocus(e);
      }

      setFocus(isKeyboard(e));
    },
    onBlur: function onBlur(e) {
      if (_onBlur) _onBlur(e);
      setFocus(false);
    },
    checked: localChecked,
    type: "checkbox",
    "data-test-id": idRef,
    disabled: disabled
  }, addInputErrorAriaProps(errorText, idRef, rest), rest, {
    id: idRef
  })), /*#__PURE__*/React.createElement(StyledCheckbox, {
    $checked: localChecked,
    $focus: focus,
    $error: !!errorText
  }, indeterminate ? /*#__PURE__*/React.createElement(MinusIndeterminate, {
    color: COLORS.WHITE
  }) : /*#__PURE__*/React.createElement(Check, {
    color: COLORS.WHITE
  })), /*#__PURE__*/React.createElement(Label, {
    htmlFor: idRef,
    "data-test-id": "".concat(idRef, "-label")
  }, /*#__PURE__*/React.createElement(React.Fragment, null, !isString && children, isString && /*#__PURE__*/React.createElement(Span, {
    className: "label-string",
    $css: css(["display:block;", " color:", ";"], TYPOGRAPHY.M_SEMIBOLD, COLORS.NEUTRAL_550)
  }, children, helperText && !errorText && /*#__PURE__*/React.createElement(HelperText, {
    $css: css(["display:block;margin-top:8px;"])
  }, helperText))))), !disableErrorContainer && (!helperText || errorText) && /*#__PURE__*/React.createElement(InputError, {
    errorText: errorText,
    errorId: idRef
  }));
};

export default Checkbox;